/**
 * Cliente de tiempo real (contrato §4, Socket.IO en el path `/realtime`).
 *
 * Reglas que el contrato fija y este módulo respeta:
 * - Auth por **token de staff** (`auth.token`) o **api key de dispositivo** (`auth.apiKey`).
 * - **El cliente no elige sus rooms de sede**: el servidor lo mete en los que le
 *   corresponden al conectar. Lo único que el cliente pide es seguir una sesión de clase
 *   o un punto de acceso concretos (`session:subscribe`, `accessPoint:subscribe`).
 * - Un solo socket por pestaña, compartido por todos los hooks.
 * - **Reconecta solo, siempre** (la tablet de recepción queda prendida todo el día,
 *   21-oct-2026): reintentos infinitos; al volver internet (`online`) o la pestaña a primer
 *   plano (`visibilitychange`) se fuerza `connect()` si está caído; y si el servidor cortó
 *   o rechazó la conexión —casos en los que Socket.IO NO reintenta por su cuenta— se
 *   vuelve a intentar igual. Al reconectar, `useRoom` re-pide sus rooms en el `connect`.
 */
import { useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { REALTIME_PATH, type RealtimeEventMap } from "@viva/shared";
import { API_URL } from "./api.js";
import { getAccessToken } from "./auth/session.js";

export type RealtimeCredentials = { token: string } | { apiKey: string };

let socket: Socket | null = null;
let currentCredentialKey = "";
let detachWatchers: (() => void) | null = null;

/** Si el servidor cortó o rechazó la conexión, Socket.IO no reintenta: se reintenta acá. */
export const MANUAL_RETRY_MS = 5_000;
/**
 * Rechazos seguidos espacian el reintento (5 s, 10 s, 20 s… hasta 1 min): un servidor que
 * reinicia vuelve enseguida, una credencial revocada no martilla para siempre.
 */
export const MANUAL_RETRY_MAX_MS = 60_000;

function credentialKeyOf(credentials: RealtimeCredentials): string {
  return "token" in credentials ? `t:${credentials.token}` : `k:${credentials.apiKey}`;
}

/** Conecta (o reusa) el socket. Si cambió la credencial, reconecta con la nueva. */
export function connectRealtime(credentials: RealtimeCredentials): Socket {
  const key = credentialKeyOf(credentials);
  if (socket && currentCredentialKey === key) return socket;
  if (socket) socket.disconnect();

  currentCredentialKey = key;
  const client = io(API_URL, {
    path: REALTIME_PATH,
    auth: credentials,
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
  });
  socket = client;
  detachWatchers?.();
  detachWatchers = watchConnection(client);
  return client;
}

/** Fuerza la reconexión cuando la tablet vuelve a tener red o a estar en primer plano. */
function watchConnection(client: Socket): () => void {
  let retry: ReturnType<typeof setTimeout> | undefined;
  let rejections = 0;
  const reconnect = () => {
    if (socket !== client || client.connected) return;
    // `active` = Socket.IO ya está reintentando por su cuenta; si no, hay que empujarlo.
    if (!client.active) client.connect();
  };
  const scheduleRetry = () => {
    clearTimeout(retry);
    const delay = Math.min(MANUAL_RETRY_MS * 2 ** rejections, MANUAL_RETRY_MAX_MS);
    rejections += 1;
    retry = setTimeout(reconnect, delay);
  };
  const onConnect = () => {
    rejections = 0;
  };
  const onVisibility = () => {
    if (document.visibilityState === "visible") reconnect();
  };
  const onDisconnect = (reason: string) => {
    // El servidor cortó la conexión: Socket.IO no la retoma solo.
    if (reason === "io server disconnect") scheduleRetry();
  };
  const onConnectError = () => {
    // Rechazada por el middleware del servidor (p. ej. un reinicio a la mitad): tampoco.
    if (!client.active) scheduleRetry();
  };

  window.addEventListener("online", reconnect);
  document.addEventListener("visibilitychange", onVisibility);
  client.on("connect", onConnect);
  client.on("disconnect", onDisconnect);
  client.on("connect_error", onConnectError);
  return () => {
    clearTimeout(retry);
    window.removeEventListener("online", reconnect);
    document.removeEventListener("visibilitychange", onVisibility);
    client.off("connect", onConnect);
    client.off("disconnect", onDisconnect);
    client.off("connect_error", onConnectError);
  };
}

export function getRealtime(): Socket | null {
  return socket;
}

export function disconnectRealtime(): void {
  detachWatchers?.();
  detachWatchers = null;
  socket?.disconnect();
  socket = null;
  currentCredentialKey = "";
}

/**
 * Suscribe un handler a un evento del contrato §4 mientras el componente vive.
 * `enabled: false` no conecta: las pantallas que no necesitan vivo no abren socket.
 */
export function useRealtimeEvent<E extends keyof RealtimeEventMap>(
  event: E,
  handler: (payload: RealtimeEventMap[E]) => void,
  options: { enabled?: boolean; credentials?: RealtimeCredentials } = {},
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const enabled = options.enabled ?? true;
  const credentials = options.credentials;

  useEffect(() => {
    if (!enabled) return;
    const token = getAccessToken();
    const creds: RealtimeCredentials | null = credentials ?? (token ? { token } : null);
    if (!creds) return;

    const client = connectRealtime(creds);
    const listener = (payload: RealtimeEventMap[E]) => handlerRef.current(payload);
    client.on(event as string, listener);
    return () => {
      client.off(event as string, listener);
    };
  }, [event, enabled, credentials]);
}

/**
 * Sigue una room que el cliente SÍ puede pedir: una sesión de clase o un punto de acceso.
 * Las rooms de sede y local las asigna el servidor — pedirlas desde acá no tendría efecto.
 */
export function useRoom(
  kind: "session" | "accessPoint",
  id: string | null | undefined,
  options: { enabled?: boolean; credentials?: RealtimeCredentials } = {},
): { connected: boolean } {
  const [connected, setConnected] = useState(false);
  const enabled = (options.enabled ?? true) && Boolean(id);
  const credentials = options.credentials;

  useEffect(() => {
    if (!enabled || !id) {
      setConnected(false);
      return;
    }
    const token = getAccessToken();
    const creds: RealtimeCredentials | null = credentials ?? (token ? { token } : null);
    if (!creds) return;

    const client = connectRealtime(creds);
    const subscribe = () => {
      client.emit(`${kind}:subscribe`, id);
      setConnected(true);
    };
    const onDisconnect = () => setConnected(false);

    if (client.connected) subscribe();
    client.on("connect", subscribe);
    client.on("disconnect", onDisconnect);

    return () => {
      client.emit(`${kind}:unsubscribe`, id);
      client.off("connect", subscribe);
      client.off("disconnect", onDisconnect);
      setConnected(false);
    };
  }, [kind, id, enabled, credentials]);

  return { connected };
}

/** Estado de la conexión, para el indicador «en vivo» de las pantallas operativas. */
export function useRealtimeStatus(credentials?: RealtimeCredentials): boolean {
  const [connected, setConnected] = useState(() => socket?.connected ?? false);

  useEffect(() => {
    const token = getAccessToken();
    const creds: RealtimeCredentials | null = credentials ?? (token ? { token } : null);
    if (!creds) return;
    const client = connectRealtime(creds);
    setConnected(client.connected);
    const on = () => setConnected(true);
    const off = () => setConnected(false);
    client.on("connect", on);
    client.on("disconnect", off);
    return () => {
      client.off("connect", on);
      client.off("disconnect", off);
    };
  }, [credentials]);

  return connected;
}
